import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Namespace names — each becomes a separate Vite chunk per locale
export const NAMESPACES = [
  'common',
  'landing',
  'auth',
  'trip',
  'expenses',
  'discover',
  'profile',
  'social',
  'rewards',
  'media',
  'tools',
  'about',
  'festival',
  'creature',
  'planner',
] as const;

export type Namespace = typeof NAMESPACES[number];

// Namespace-to-top-level-key mapping (used externally for route-level preloading)
export const NAMESPACE_KEYS: Record<Namespace, string[]> = {
  common: ['nav', 'footer', 'common', 'sidebar', 'navbar', 'pwa', 'theme', 'emptyStates', 'tiers', 'queryError', 'bottomNav', 'fab', 'globalSearch'],
  landing: ['hero', 'valueProps', 'howItWorks', 'destinations', 'featuresDeep', 'cta', 'midCta', 'waitlist', 'thankYou', 'festivalJoin', 'festivalCard', 'festivalProfile', 'festivalGuide'],
  auth: ['signup', 'consent', 'profileCompletion'],
  trip: ['tripDetail', 'createTrip', 'tripPlanner', 'tripGeneration', 'tripVisibility', 'tripLogistics', 'tripCalendarView', 'tripConversation', 'tripMap', 'tripMatching', 'sharedTrip', 'tripGuide', 'bookingConcierge', 'experienceScorecard', 'crewChemistry', 'compatibilityInsights', 'tripPsychology', 'firstImpression', 'crewNorms', 'whatsNext'],
  expenses: ['tripExpenses', 'expenses', 'budget', 'budgetValidation', 'decisionEngine', 'costSummary', 'quickExpense', 'transactionHistory', 'transactions'],
  discover: ['discover', 'myTrips', 'dashboard', 'aiPlanner', 'browse', 'dailyGuide', 'localEvents', 'travelDna', 'dailyGuideCompanion'],
  profile: ['profile', 'settings', 'friends', 'block', 'messages', 'privacy', 'faith', 'milestones', 'trust', 'byo'],
  social: ['community', 'reviews', 'favorites', 'favoritesLeaderboard', 'notifications', 'referrals', 'moderation', 'checkin'],
  rewards: ['pointsMiles', 'tripPointsMiles', 'rewards', 'premium', 'creditStore', 'usageLimit'],
  media: ['tripPhotos', 'allPhotos', 'shareDialog', 'sendDialog', 'saveIdea', 'travelerCard', 'swipeDeck', 'routes'],
  tools: ['packing', 'transportSegment', 'airportSearch', 'placeSearch', 'travelCalendar', 'safety', 'notFound', 'resources'],
  about: ['founder', 'dreamTrip', 'cocora', 'mission', 'science', 'whatWeDo', 'contact'],
  festival: ['joinPage'],
  creature: ['title', 'hatch', 'hatchDescription', 'name', 'rename', 'renamePlaceholder', 'stages', 'stageDescriptions', 'slots', 'trunk', 'meeting', 'checkin', 'xp', 'chat', 'fab', 'swap'],
  planner: ['levelSet', 'category', 'subcategory', 'browse', 'advanced', 'startOutline', 'parade', 'divider', 'swap'],
};

// Dynamic import loaders per locale per namespace — each produces a separate Vite chunk
type NamespaceLoader = () => Promise<{ default: Record<string, unknown> }>;
type LocaleLoaders = Record<Namespace, NamespaceLoader>;

const namespaceLoaders: Record<string, LocaleLoaders> = {
  en: {
    common:    () => import('./locales/en/common.json'),
    landing:   () => import('./locales/en/landing.json'),
    auth:      () => import('./locales/en/auth.json'),
    trip:      () => import('./locales/en/trip.json'),
    expenses:  () => import('./locales/en/expenses.json'),
    discover:  () => import('./locales/en/discover.json'),
    profile:   () => import('./locales/en/profile.json'),
    social:    () => import('./locales/en/social.json'),
    rewards:   () => import('./locales/en/rewards.json'),
    media:     () => import('./locales/en/media.json'),
    tools:     () => import('./locales/en/tools.json'),
    about:     () => import('./locales/en/about.json'),
    festival:  () => import('./locales/en/festival.json'),
    creature:  () => import('./locales/en/creature.json'),
    planner:   () => import('./locales/en/planner.json'),
  },
  es: {
    common:    () => import('./locales/es/common.json'),
    landing:   () => import('./locales/es/landing.json'),
    auth:      () => import('./locales/es/auth.json'),
    trip:      () => import('./locales/es/trip.json'),
    expenses:  () => import('./locales/es/expenses.json'),
    discover:  () => import('./locales/es/discover.json'),
    profile:   () => import('./locales/es/profile.json'),
    social:    () => import('./locales/es/social.json'),
    rewards:   () => import('./locales/es/rewards.json'),
    media:     () => import('./locales/es/media.json'),
    tools:     () => import('./locales/es/tools.json'),
    about:     () => import('./locales/es/about.json'),
    festival:  () => import('./locales/es/festival.json'),
    creature:  () => import('./locales/es/creature.json'),
    planner:   () => import('./locales/es/planner.json'),
  },
  fr: {
    common:    () => import('./locales/fr/common.json'),
    landing:   () => import('./locales/fr/landing.json'),
    auth:      () => import('./locales/fr/auth.json'),
    trip:      () => import('./locales/fr/trip.json'),
    expenses:  () => import('./locales/fr/expenses.json'),
    discover:  () => import('./locales/fr/discover.json'),
    profile:   () => import('./locales/fr/profile.json'),
    social:    () => import('./locales/fr/social.json'),
    rewards:   () => import('./locales/fr/rewards.json'),
    media:     () => import('./locales/fr/media.json'),
    tools:     () => import('./locales/fr/tools.json'),
    about:     () => import('./locales/fr/about.json'),
    festival:  () => import('./locales/fr/festival.json'),
    creature:  () => import('./locales/fr/creature.json'),
    planner:   () => import('./locales/fr/planner.json'),
  },
  de: {
    common:    () => import('./locales/de/common.json'),
    landing:   () => import('./locales/de/landing.json'),
    auth:      () => import('./locales/de/auth.json'),
    trip:      () => import('./locales/de/trip.json'),
    expenses:  () => import('./locales/de/expenses.json'),
    discover:  () => import('./locales/de/discover.json'),
    profile:   () => import('./locales/de/profile.json'),
    social:    () => import('./locales/de/social.json'),
    rewards:   () => import('./locales/de/rewards.json'),
    media:     () => import('./locales/de/media.json'),
    tools:     () => import('./locales/de/tools.json'),
    about:     () => import('./locales/de/about.json'),
    festival:  () => import('./locales/de/festival.json'),
    creature:  () => import('./locales/de/creature.json'),
    planner:   () => import('./locales/de/planner.json'),
  },
  pt: {
    common:    () => import('./locales/pt/common.json'),
    landing:   () => import('./locales/pt/landing.json'),
    auth:      () => import('./locales/pt/auth.json'),
    trip:      () => import('./locales/pt/trip.json'),
    expenses:  () => import('./locales/pt/expenses.json'),
    discover:  () => import('./locales/pt/discover.json'),
    profile:   () => import('./locales/pt/profile.json'),
    social:    () => import('./locales/pt/social.json'),
    rewards:   () => import('./locales/pt/rewards.json'),
    media:     () => import('./locales/pt/media.json'),
    tools:     () => import('./locales/pt/tools.json'),
    about:     () => import('./locales/pt/about.json'),
    festival:  () => import('./locales/pt/festival.json'),
    creature:  () => import('./locales/pt/creature.json'),
    planner:   () => import('./locales/pt/planner.json'),
  },
};

// Custom backend: loads namespace JSON files as separate dynamic-import chunks.
// The namespace name is passed through; each import() call becomes its own Vite chunk.
// Existing t('someTopLevelKey.subKey') calls continue to work because i18next searches
// all loaded namespaces via the fallbackNS chain.
const NamespaceBackend = {
  type: 'backend' as const,
  init() {},
  read(
    language: string,
    namespace: string,
    callback: (err: Error | null, data?: Record<string, unknown>) => void,
  ) {
    const localeLoaders = namespaceLoaders[language];
    if (!localeLoaders) {
      callback(new Error(`No loaders for language: ${language}`));
      return;
    }
    const loader = localeLoaders[namespace as Namespace];
    if (!loader) {
      callback(new Error(`No loader for ${language}/${namespace}`));
      return;
    }
    loader()
      .then((mod) => callback(null, mod.default))
      .catch((err: Error) => callback(err));
  },
};

// Load all English namespaces eagerly (en is the fallback language — must be synchronous
// to avoid flash of untranslated content on first render). We pre-bundle English common
// and load the rest on demand in non-English builds; for English we load all to avoid
// key-not-found fallback flickering.
async function loadEnglishResources(): Promise<Record<string, Record<string, unknown>>> {
  const results = await Promise.all(
    NAMESPACES.map(async (ns) => {
      const mod = await namespaceLoaders.en[ns]();
      return [ns, mod.default] as const;
    }),
  );
  return Object.fromEntries(results);
}

// We synchronously seed the English `common` namespace so the app renders immediately,
// then async-load the rest of English and all other locales on demand.
import enCommon from './locales/en/common.json';

const enCommonResources: Record<string, Record<string, unknown>> = {
  common: enCommon,
};

i18n
  .use(NamespaceBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Seed English common synchronously so the app is never blank on first render.
    // All other English namespaces and all non-English locales are loaded by the backend.
    resources: { en: enCommonResources },
    partialBundledLanguages: true,
    ns: NAMESPACES as unknown as string[],
    defaultNS: 'common',
    // Fall back through all namespaces so t('tripDetail.x') works regardless of which
    // namespace the caller is using. i18next searches ns array in order on miss.
    fallbackNS: NAMESPACES as unknown as string[],
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
    react: {
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      useSuspense: false,
    },
  });

// After init, load remaining English namespaces so all keys are available before the
// user interacts. This runs async and does not block the initial render.
loadEnglishResources().then((nsData) => {
  for (const [ns, data] of Object.entries(nsData)) {
    if (ns !== 'common') {
      i18n.addResourceBundle('en', ns, data, true, false);
    }
  }
});

export default i18n;
